/**
 * Axios interceptors for OpenTelemetry-related headers (x-flow-id) on itinerary APIs.
 *
 * Phase 2 (optional): add @opentelemetry instrumentation here if browser W3C propagation is required.
 */

import axios, { AxiosHeaders, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { appendCorrelationDiagnosticEntry } from "./correlationDiagnosticBuffer";
import {
    applyFlowIdFromResponse,
    getOffRouteRecalculationFlowIdForRequests,
    getPlannerItineraryFlowIdForRequests,
} from "./correlationFlowStore";
import type { FlowCorrelationScope } from "./flowCorrelationScope";

const FLOW_HEADER_URLS: readonly string[] = [
    "/api/itinerary/search",
    "/api/itinerary/completion",
    "/api/pricing/evaluation",
    "/api/sales/offer",
];

const urlNeedsFlowCorrelation = ( url: string | undefined ): boolean => {
    if ( url === undefined || url.length === 0 ) {
        return false;
    }

    return FLOW_HEADER_URLS.some( ( path ) => url.includes( path ) );
};

const resolveScope = ( config: InternalAxiosRequestConfig ): FlowCorrelationScope => {
    if ( typeof config.url === "string" && config.url.includes( "/api/itinerary/search" ) ) {
        return config.flowCorrelationScope === "offRoute" ? "offRoute" : "planner";
    }

    return "planner";
};

const flowIdForScope = ( scope: FlowCorrelationScope ): string | null => {
    return scope === "offRoute"
        ? getOffRouteRecalculationFlowIdForRequests()
        : getPlannerItineraryFlowIdForRequests();
};

const readHeader = ( headers: unknown, canonical: string ): string | undefined => {
    if ( headers === undefined || headers === null ) {
        return undefined;
    }
    if ( typeof ( headers as AxiosHeaders ).get === "function" ) {
        const h = headers as AxiosHeaders;
        const a = h.get( canonical );
        const b = h.get( canonical.toLowerCase() );
        const v = typeof a === "string" && a.length > 0 ? a : b;
        if ( typeof v === "string" && v.length > 0 ) {
            return v;
        }

        return undefined;
    }
    const record = headers as Record<string, unknown>;
    const lower = canonical.toLowerCase();
    const direct = record[canonical] ?? record[lower];
    if ( typeof direct === "string" && direct.length > 0 ) {
        return direct;
    }

    return undefined;
};

const extractPath = ( config: InternalAxiosRequestConfig ): string => {
    const raw = config.url ?? "";
    const q = raw.indexOf( "?" );

    return q === -1 ? raw : raw.slice( 0, q );
};

let interceptorsRegistered = false;

/**
 * Attaches request/response interceptors once (guarded for HMR).
 */
export const registerFlowCorrelationInterceptors = ( instance: AxiosInstance ): void => {
    if ( interceptorsRegistered ) {
        return;
    }
    interceptorsRegistered = true;

    instance.interceptors.request.use( ( config: InternalAxiosRequestConfig ) => {
        if ( urlNeedsFlowCorrelation( config.url ) === false ) {
            return config;
        }
        const scope = resolveScope( config );
        const flowId = flowIdForScope( scope );
        if ( flowId === null || flowId.length === 0 ) {
            return config;
        }
        const headers = AxiosHeaders.from( config.headers ?? {} );
        if ( headers.has( "x-flow-id" ) === false ) {
            headers.set( "x-flow-id", flowId );
            config.headers = headers;
        }

        return config;
    } );

    instance.interceptors.response.use(
        ( response ) => {
            const config = response.config;
            if ( urlNeedsFlowCorrelation( config.url ) === false ) {
                return response;
            }
            const scope = resolveScope( config );
            const flowSentBefore = flowIdForScope( scope );
            const headerSource = response.headers;
            const flowEcho = readHeader( headerSource, "x-flow-id" );
            const traceEcho = readHeader( headerSource, "x-trace-id" );
            const traceparentEcho = readHeader( headerSource, "traceparent" );
            if ( flowEcho !== undefined ) {
                applyFlowIdFromResponse( scope, flowEcho );
            }
            appendCorrelationDiagnosticEntry( {
                "t":      new Date().toISOString(),
                "method": ( config.method ?? "get" ).toUpperCase(),
                "path":   extractPath( config ),
                "status": response.status,
                scope,
                ...flowSentBefore !== null && flowSentBefore.length > 0 ? { "flowIdSent": flowSentBefore } : {},
                ...flowEcho !== undefined ? { "flowId": flowEcho } : {},
                ...traceEcho !== undefined ? { "traceId": traceEcho } : {},
                ...traceparentEcho !== undefined ? { "traceparent": traceparentEcho } : {},
            } );

            return response;
        },
        ( error: unknown ) => {
            if ( axios.isAxiosError( error ) && error.config !== undefined ) {
                const config = error.config;
                if ( urlNeedsFlowCorrelation( config.url ) ) {
                    const scope = resolveScope( config );
                    const flowSentBefore = flowIdForScope( scope );
                    const status = error.response?.status ?? null;
                    const headerSource = error.response?.headers;
                    const flowEcho = readHeader( headerSource, "x-flow-id" );
                    const traceEcho = readHeader( headerSource, "x-trace-id" );
                    const traceparentEcho = readHeader( headerSource, "traceparent" );
                    if ( flowEcho !== undefined ) {
                        applyFlowIdFromResponse( scope, flowEcho );
                    }
                    appendCorrelationDiagnosticEntry( {
                        "t":      new Date().toISOString(),
                        "method": ( config.method ?? "get" ).toUpperCase(),
                        "path":   extractPath( config ),
                        status,
                        scope,
                        ...flowSentBefore !== null && flowSentBefore.length > 0 ? { "flowIdSent": flowSentBefore } : {},
                        ...flowEcho !== undefined ? { "flowId": flowEcho } : {},
                        ...traceEcho !== undefined ? { "traceId": traceEcho } : {},
                        ...traceparentEcho !== undefined ? { "traceparent": traceparentEcho } : {},
                    } );
                }
            }

            const normalizedError = error instanceof Error
                ? error
                : new Error( "Correlation itinerary axios interceptor rejected a non-Error value." );

            return Promise.reject( normalizedError );
        },
    );
};
